import { useState, useEffect, useRef } from 'react';

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Animates a set of numeric values from 0 → target when the
 * returned `containerRef` enters the viewport.
 *
 * Key design: the IntersectionObserver effect runs ONCE ([] deps).
 * The animation frame is only cancelled on component unmount — never
 * mid-run due to a state update re-triggering the cleanup.
 */
export function useCountUp(
  targets: Record<string, number>,
  duration = 1600
) {
  const [values, setValues] = useState<Record<string, number>>(
    () => Object.fromEntries(Object.keys(targets).map(k => [k, 0]))
  );
  const [hasStarted, setHasStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef       = useRef<number | null>(null);

  // Keep a ref to targets so the animation always reads the latest values
  // without needing targets as a useEffect dependency.
  const targetsRef = useRef(targets);
  useEffect(() => { targetsRef.current = targets; }, [targets]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        observer.disconnect();
        setHasStarted(true);

        // Start the count-up directly inside the callback so no
        // dependency change can trigger a cleanup mid-animation.
        const startTime = performance.now();

        const tick = (now: number) => {
          const elapsed  = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const ease     = easeOut(progress);

          setValues(
            Object.fromEntries(
              Object.entries(targetsRef.current).map(([k, v]) => [
                k,
                Math.round(v * ease),
              ])
            )
          );

          if (progress < 1) {
            rafRef.current = requestAnimationFrame(tick);
          }
        };

        rafRef.current = requestAnimationFrame(tick);
      },
      { threshold: 0.2 }
    );

    observer.observe(el);

    // Cleanup only on unmount — never fires during a normal re-render.
    return () => {
      observer.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { values, containerRef, hasStarted };
}
